import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar 
} from "recharts";

export default function AnalyticsDashboard({ user }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalScans: 0, uniqueAssets: 0, topCampaign: "None" });
  const [timeData, setTimeData] = useState([]);
  const [deviceData, setDeviceData] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);

  const COLORS = ["#dc2626", "#475569", "#1e293b", "#ef4444"];

  useEffect(() => {
    if (user) fetchGlobalAnalytics();
  }, [user]);

const fetchGlobalAnalytics = async () => {
  setLoading(true);
  const timeout = setTimeout(() => setLoading(false), 8000);
  try {
    const { data: userQRs, error: qrError } = await supabase
      .from("qr_codes").select("id, name").eq("user_id", user.id);

    if (qrError) throw qrError;
    if (!userQRs?.length) {
      setStats({ totalScans: 0, uniqueAssets: 0, topCampaign: "None" });
      setLoading(false);
      return;
    }

    const qrIds = userQRs.map(qr => qr.id);
    const qrMap = userQRs.reduce((acc, qr) => ({ ...acc, [qr.id]: qr.name }), {});

    const { data: logs, error: logError } = await supabase
      .from("qr_logs")
      .select("scanned_at, device_type, qr_id, hour_of_day")
      .in("qr_id", qrIds)
      .order("scanned_at", { ascending: true });

    if (logError) throw logError;

    if (logs?.length > 0) {
      const campaignCounts = logs.reduce((acc, log) => {
        const name = qrMap[log.qr_id] || "Unknown";
        acc[name] = (acc[name] || 0) + 1;
        return acc;
      }, {});

      const topCmp = Object.keys(campaignCounts).reduce((a, b) =>
        campaignCounts[a] > campaignCounts[b] ? a : b);

      setStats({ totalScans: logs.length, uniqueAssets: userQRs.length, topCampaign: topCmp });

      // Timeline — fix: use scanned_at not created_at
      const timeGrouped = logs.reduce((acc, log) => {
        const date = new Date(log.scanned_at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});
      setTimeData(Object.entries(timeGrouped).map(([date, scans]) => ({ date, scans })));

      // Devices
      const devices = logs.reduce((acc, log) => {
        const type = log.device_type || "Mobile";
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      setDeviceData(Object.keys(devices).map(type => ({ name: type, value: devices[type] })));

      // Leaderboard
      setLeaderboard(Object.entries(campaignCounts)
        .map(([name, scans]) => ({ name, scans }))
        .sort((a, b) => b.scans - a.scans).slice(0, 5));

      // Hourly heatmap
      const hourlyMap = Array(24).fill(0);
      logs.forEach(log => {
        const h = log.hour_of_day ?? new Date(log.scanned_at).getHours();
        hourlyMap[h]++;
      });
      setHourlyData(hourlyMap.map((count, h) => ({
        hour: h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h-12}pm`,
        count
      })));
    }
  } catch (err) {
    console.error("Analytics Engine Error:", err);
  } finally {
    clearTimeout(timeout);
    setLoading(false);
  }
};

  if (loading) return (
    <div className="h-96 flex flex-col items-center justify-center gap-4">
      <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      <p className="text-[10px] font-black uppercase tracking-[8px] text-red-600 animate-pulse">Processing Neural Data...</p>
    </div>
  );

  // Empty State View
  if (stats.totalScans === 0) return (
    <div className="h-96 flex flex-col items-center justify-center border border-white/5 bg-[#0a0a0a] rounded-[40px]">
      <span className="text-4xl mb-4">📡</span>
      <h3 className="text-white font-black uppercase italic tracking-tighter">No Traffic Detected</h3>
      <p className="text-gray-500 text-[10px] font-bold uppercase tracking-widest mt-2">Scan your QR codes to generate live intelligence</p>
    </div>
  );

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-1000">
      
      {/* HEADER STATS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard label="Total Performance" value={stats.totalScans} suffix="Hits" color="text-red-600" />
        <StatCard label="Active Assets" value={stats.uniqueAssets} suffix="Active" color="text-white" />
        <StatCard label="MVP Campaign" value={stats.topCampaign.split(' ').slice(0,2).join(' ')} suffix="Top" color="text-red-500" />
      </div>
      
      {/* HOURLY HEATMAP */}
    {hourlyData?.length > 0 && hourlyData.some(h => h.count > 0) && (
        <div className="bg-[#0a0a0a] border border-white/5 p-8 rounded-[40px]">
          <div className="mb-6">
            <h3 className="text-white text-lg font-black italic uppercase tracking-tighter">
              Peak Scan Hours
            </h3>
            <p className="text-gray-500 text-[9px] font-bold tracking-[5px] uppercase">
              All campaigns combined
            </p>
          </div>

          <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
                <XAxis dataKey="hour" stroke="#475569" fontSize={8} tickLine={false} axisLine={false} interval={2} />
                <YAxis stroke="#475569" fontSize={9} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ backgroundColor: "#000", border: "none", borderRadius: 10, fontSize: 10 }} />
                <Bar dataKey="count" fill="#dc2626" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* MAIN CHART: SCAN VELOCITY */}
        <div className="lg:col-span-2 bg-[#0a0a0a] border border-white/5 p-8 rounded-[40px] shadow-2xl">
          <div className="mb-8">
            <h3 className="text-white text-lg font-black italic uppercase tracking-tighter">Growth Velocity</h3>
            <p className="text-gray-500 text-[9px] font-bold tracking-[5px] uppercase">Aggregated Traffic Flow</p>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeData}>
                <defs>
                  <linearGradient id="colorScans" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc2626" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff05" vertical={false} />
                <XAxis dataKey="date" stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
                <YAxis stroke="#475569" fontSize={10} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#000', border: '1px solid #ffffff10', borderRadius: '15px', fontSize: '10px' }}
                  itemStyle={{ color: '#dc2626', fontWeight: 'bold' }}
                />
                <Area type="monotone" dataKey="scans" stroke="#dc2626" strokeWidth={4} fillOpacity={1} fill="url(#colorScans)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* PIE CHART: DEVICE MIX */}
        <div className="bg-[#0a0a0a] border border-white/5 p-8 rounded-[40px] flex flex-col items-center">
          <div className="w-full mb-8">
            <h3 className="text-white text-lg font-black italic uppercase tracking-tighter text-left">Device Mix</h3>
            <p className="text-gray-500 text-[9px] font-bold tracking-[5px] uppercase">Tech Stack Insights</p>
          </div>
          <div className="h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={deviceData} innerRadius={60} outerRadius={80} paddingAngle={8} dataKey="value">
                  {deviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#000', border: 'none', borderRadius: '10px', fontSize: '10px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-6 space-y-2 w-full">
            {deviceData.map((d, i) => (
              <div key={i} className="flex justify-between items-center bg-white/5 p-2 px-4 rounded-xl border border-white/5">
                <span className="text-[10px] font-black uppercase text-slate-400">{d.name}</span>
                <span className="text-xs font-black text-white">{d.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* LEADERBOARD BAR CHART */}
        <div className="lg:col-span-3 bg-[#0a0a0a] border border-white/5 p-8 rounded-[40px]">
          <div className="mb-6">
            <h3 className="text-white text-lg font-black italic uppercase tracking-tighter">Campaign Leaderboard</h3>
            <p className="text-gray-500 text-[9px] font-bold tracking-[5px] uppercase">Top 5 Performing Assets</p>
          </div>
          <div className="h-48 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={leaderboard} layout="vertical">
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" stroke="#475569" fontSize={10} width={100} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: 'transparent' }} contentStyle={{ backgroundColor: '#000', border: 'none' }} />
                <Bar dataKey="scans" fill="#dc2626" radius={[0, 10, 10, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
}

function StatCard({ label, value, suffix, color }) {
  return (
    <div className="bg-[#0a0a0a] border border-white/5 p-6 rounded-[32px] shadow-xl transition-transform hover:scale-105">
      <p className="text-gray-600 text-[9px] font-black uppercase tracking-[5px] mb-2">{label}</p>
      <div className="flex items-baseline gap-2">
        <span className={`text-4xl font-black italic tracking-tighter ${color}`}>{value}</span>
        <span className="text-[10px] font-black text-slate-700 uppercase">{suffix}</span>
      </div>
      </div>
  );
}